import { Icon, Link, Typography } from '@/components';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import type { AvatarDropdownItem } from '@/features/header/components/avatar-dropdown';

export type NavUserInfo = {
  readonly firstName: string;
  readonly lastName: string;
  readonly fullName: string;
  readonly avatar: string;
  readonly email: string;
  readonly company: string;
};

export type NavUserViewProps = {
  readonly options: AvatarDropdownItem[];
  readonly userInfo: NavUserInfo;

  readonly dropdownSide: 'bottom' | 'right';

  readonly showUpgrade: boolean;
  readonly upgradeToProHref?: string;

  readonly onLogout: () => void;
};

export function NavUserView({
  options,
  userInfo,
  dropdownSide,
  showUpgrade,
  upgradeToProHref,
  onLogout,
}: NavUserViewProps) {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:text-sidebar-accent-foreground hover:cursor-pointer data-[state=open]:bg-black/15"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={userInfo.avatar} alt={userInfo.fullName} />
                <AvatarFallback className="rounded-lg">CN</AvatarFallback>
              </Avatar>

              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate">{userInfo.fullName}</span>
                <span className="truncate text-xs font-bold">{userInfo.company}</span>
              </div>

              <Icon name="chevrons-up-down" className="ml-auto h-4 w-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg border border-neutral-50 bg-white p-0.5 shadow-md"
            side={dropdownSide}
            align="end"
            sideOffset={8}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={userInfo.avatar} alt={userInfo.fullName} />
                  <AvatarFallback className="rounded-lg">CN</AvatarFallback>
                </Avatar>

                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{userInfo.fullName}</span>
                  <span className="truncate text-xs">{userInfo.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <ul>
              {options.map(({ label, icon, onClick }: AvatarDropdownItem) => (
                <li
                  key={`${icon}-${label}`}
                  className="group hover:bg-primary-light flex cursor-pointer items-center gap-2.5 bg-white px-1.5 py-1 transition"
                  onClick={onClick ?? undefined}
                >
                  {icon ? (
                    <Icon name={icon} className="group-hover:text-primary-700 h-4 w-4 text-neutral-300 transition" />
                  ) : null}

                  <Typography as="span" size="body-3" color="primary" weight="normal">
                    {label}
                  </Typography>
                </li>
              ))}

              {showUpgrade && upgradeToProHref ? (
                <li className="mb-1 p-2">
                  <Link
                    to={upgradeToProHref}
                    target="_self"
                    className="border-secondary-accent fill-secondary-accent text-secondary-accent hover:bg-secondary-accent hover:fill-secondary-accent active:bg-secondary-accent active:fill-secondary-accent flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-solid bg-white px-2 py-1.5 text-center text-base font-semibold transition select-none hover:text-white hover:no-underline active:text-white"
                  >
                    <span className="flex items-center justify-center gap-x-1">
                      <Icon name="lightning" className="h-4 w-4 fill-inherit text-inherit" />
                      Upgrade to PRO
                    </span>
                  </Link>
                </li>
              ) : null}

              <li
                className="group hover:bg-primary-light mt-2 flex cursor-pointer items-center gap-2.5 rounded-b-md border-t border-t-neutral-50 bg-white px-1.5 py-1 transition"
                onClick={onLogout}
              >
                <Icon name="log-out" className="group-hover:text-primary-700 h-4 w-4 text-neutral-300 transition" />
                <Typography as="span" size="body-3" color="primary" weight="normal">
                  Log Out
                </Typography>
              </li>
            </ul>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
